import { useState, useMemo, useCallback, useEffect } from 'react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Text } from '@mantine/core';
import type { Participant } from '../../../../types';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { groupsApi } from '../../../../services/api/groups';
import {
  participantToDraggable,
  type DraggableParticipantData,
} from '../DraggableParticipantCard';

export interface PendingModalAssignment {
  participant: DraggableParticipantData;
  targetGroup: GroupWithMembers;
}

interface UseParticipantAssignmentOptions {
  cohortId: string;
  groups: GroupWithMembers[];
  participants: Participant[];
  onRefresh: () => void | Promise<void>;
  /** When set, intercepts assignment for confirmation before executing */
  onBeforeAssign?: (pending: PendingModalAssignment) => boolean;
}

export interface UseParticipantAssignmentResult {
  /** Participants not yet assigned to any group */
  unassignedParticipants: Participant[];
  /** Group options formatted for Mantine Select */
  groupOptions: Array<{ value: string; label: string }>;

  // ── Single assignment state ──
  selectedParticipantForAssign: DraggableParticipantData | null;
  setSelectedParticipantForAssign: (p: DraggableParticipantData | null) => void;
  selectedGroupId: string | null;
  setSelectedGroupId: (id: string | null) => void;
  assigning: boolean;
  /** Initiate the "assign to group" flow for a single participant */
  handleAssignToGroup: (participant: DraggableParticipantData) => void;
  /** Execute the single assignment (called from the modal confirm button) */
  executeAssignment: () => Promise<void>;
  /** Select options derived from unassigned participants */
  getParticipantSelectOptions: () => Array<{ value: string; label: string }>;
  /** Handle selecting a participant from the Select dropdown inside the assign modal */
  handleSelectParticipantFromDropdown: (value: string | null) => void;

  // ── Add participant to a specific group ──
  handleAddParticipantToGroup: (group: GroupWithMembers) => void;

  // ── Multi-select / bulk ──
  selectionMode: boolean;
  selectedParticipants: Set<string>;
  handleSelectParticipant: (participant: DraggableParticipantData, selected: boolean) => void;
  handleSelectAll: (selected: boolean) => void;
  handleBulkAssign: (groupId: string) => Promise<void>;

  // ── Member operations ──
  handleRemoveMemberFromGroup: (
    group: GroupWithMembers,
    member: { user_id: string; first_name: string; last_name: string },
  ) => Promise<void>;
}

/**
 * Manages all participant-to-group assignment logic: single assignment,
 * bulk assignment with capacity checks, and member removal.
 */
export function useParticipantAssignment({
  cohortId,
  groups,
  participants,
  onRefresh,
  onBeforeAssign,
}: UseParticipantAssignmentOptions): UseParticipantAssignmentResult {
  // ── Single assignment ──
  const [selectedParticipantForAssign, setSelectedParticipantForAssign] =
    useState<DraggableParticipantData | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  // ── Multi-select ──
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());

  // ── Derived data ──
  const unassignedParticipants = useMemo(
    () => participants.filter((p) => !p.group_id && p.status !== 'withdrawn'),
    [participants],
  );

  // Fetch ALL groups for the dropdown (not just paginated ones)
  const [allGroups, setAllGroups] = useState<GroupWithMembers[]>([]);
  const assignModalActive = selectedParticipantForAssign !== null || selectedGroupId !== null;

  // Stable signature of the parent groups list. When the parent calls
  // onRefresh() after a group create/delete, this signature changes and the
  // dropdown's allGroups refetch fires — keeping the dropdown in sync without
  // a page reload.
  const groupsSignature = useMemo(
    () => groups.map((g) => `${g.id}:${g.member_count}`).join(','),
    [groups],
  );

  useEffect(() => {
    if (!assignModalActive) {
      // Clear cached fetch when modal becomes inactive so the next open
      // always sees fresh data — Mantine's modal close does not reset the
      // hook's selection state, so we cannot rely on assignModalActive
      // toggling cleanly between every open.
      setAllGroups((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let cancelled = false;
    groupsApi.list(cohortId, { limit: 500, offset: 0 }).then((res) => {
      if (!cancelled) setAllGroups(res.data);
    });
    return () => { cancelled = true; };
  }, [assignModalActive, cohortId, groupsSignature]);

  const groupOptions = useMemo(
    () => {
      const source = allGroups.length > 0 ? allGroups : groups;
      return source.map((g) => ({
        value: g.id,
        label: `${g.name} (${g.member_count})`,
      }));
    },
    [allGroups, groups],
  );

  const getParticipantSelectOptions = useCallback(
    () =>
      unassignedParticipants.map((p) => ({
        value: p.user_id,
        label: `${p.first_name} ${p.last_name}`,
      })),
    [unassignedParticipants],
  );

  const handleSelectParticipantFromDropdown = useCallback(
    (value: string | null) => {
      const p = unassignedParticipants.find((up) => up.user_id === value);
      if (p) {
        setSelectedParticipantForAssign(participantToDraggable(p));
      }
    },
    [unassignedParticipants],
  );

  // ── Handlers ──
  const handleAssignToGroup = useCallback((participant: DraggableParticipantData) => {
    setSelectedParticipantForAssign(participant);
    setSelectedGroupId(null);
  }, []);

  const executeAssignment = useCallback(async () => {
    if (!selectedParticipantForAssign || !selectedGroupId) return;

    // Intercept for confirmation modal (MED-620)
    const targetGroup = allGroups.find((g) => g.id === selectedGroupId) || groups.find((g) => g.id === selectedGroupId);
    if (targetGroup && onBeforeAssign?.({ participant: selectedParticipantForAssign, targetGroup })) {
      return;
    }

    setAssigning(true);
    try {
      await groupsApi.assignMember(cohortId, selectedGroupId, selectedParticipantForAssign.user_id);
      notifications.show({
        title: 'Participant Assigned',
        message: `${selectedParticipantForAssign.first_name} ${selectedParticipantForAssign.last_name} has been assigned to the group`,
        color: 'green',
      });
      setSelectedParticipantForAssign(null);
      setSelectedGroupId(null);
      await onRefresh();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to assign participant',
        color: 'red',
      });
    } finally {
      setAssigning(false);
    }
  }, [cohortId, selectedParticipantForAssign, selectedGroupId, onRefresh, allGroups, groups, onBeforeAssign]);

  const handleAddParticipantToGroup = useCallback(
    (group: GroupWithMembers) => {
      if (unassignedParticipants.length === 0) {
        notifications.show({
          title: 'No Unassigned Participants',
          message: 'All participants are already assigned to groups',
          color: 'blue',
        });
        return;
      }
      setSelectedGroupId(group.id);
      setSelectedParticipantForAssign(null);
    },
    [unassignedParticipants],
  );

  // ── Multi-select handlers ──
  const handleSelectParticipant = useCallback(
    (participant: DraggableParticipantData, selected: boolean) => {
      setSelectedParticipants((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(participant.user_id);
        } else {
          next.delete(participant.user_id);
        }
        return next;
      });
    },
    [],
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedParticipants(new Set(unassignedParticipants.map((p) => p.user_id)));
      } else {
        setSelectedParticipants(new Set());
      }
    },
    [unassignedParticipants],
  );

  const executeBulkAssign = useCallback(
    async (groupId: string, limit?: number) => {
      const userIds = Array.from(selectedParticipants);
      const toAssign = limit ? userIds.slice(0, limit) : userIds;

      setAssigning(true);
      try {
        for (const userId of toAssign) {
          await groupsApi.assignMember(cohortId, groupId, userId);
        }

        notifications.show({
          title: 'Participants Assigned',
          message: `${toAssign.length} participant${toAssign.length !== 1 ? 's' : ''} assigned to group`,
          color: 'green',
        });

        setSelectedParticipants(new Set());
        setSelectionMode(false);
        await onRefresh();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to assign participants',
          color: 'red',
        });
      } finally {
        setAssigning(false);
      }
    },
    [cohortId, selectedParticipants, onRefresh],
  );

  const handleBulkAssign = useCallback(
    async (groupId: string) => {
      if (selectedParticipants.size === 0) return;

      const group = groups.find((g) => g.id === groupId);
      if (!group) return;

      const availableSlots = (group.max_size || 6) - group.member_count;
      if (selectedParticipants.size > availableSlots) {
        modals.openConfirmModal({
          title: 'Capacity Warning',
          children: (
            <Text size="sm">
              {group.name} only has {availableSlots} available slot
              {availableSlots !== 1 ? 's' : ''}, but you selected{' '}
              {selectedParticipants.size} participant
              {selectedParticipants.size !== 1 ? 's' : ''}. Only the first{' '}
              {availableSlots} will be assigned.
            </Text>
          ),
          labels: { confirm: 'Continue', cancel: 'Cancel' },
          onConfirm: () => executeBulkAssign(groupId, availableSlots),
        });
      } else {
        await executeBulkAssign(groupId);
      }
    },
    [selectedParticipants, groups, executeBulkAssign],
  );

  // ── Member removal ──
  const handleRemoveMemberFromGroup = useCallback(
    async (
      group: GroupWithMembers,
      member: { user_id: string; first_name: string; last_name: string },
    ) => {
      try {
        await groupsApi.removeMember(cohortId, group.id, member.user_id);
        notifications.show({
          title: 'Participant Removed',
          message: `${member.first_name} ${member.last_name} has been removed from ${group.name}`,
          color: 'green',
        });
        await onRefresh();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to remove participant from group',
          color: 'red',
        });
      }
    },
    [cohortId, onRefresh],
  );

  return {
    unassignedParticipants,
    groupOptions,
    selectedParticipantForAssign,
    setSelectedParticipantForAssign,
    selectedGroupId,
    setSelectedGroupId,
    assigning,
    handleAssignToGroup,
    executeAssignment,
    getParticipantSelectOptions,
    handleSelectParticipantFromDropdown,
    handleAddParticipantToGroup,
    selectionMode,
    selectedParticipants,
    handleSelectParticipant,
    handleSelectAll,
    handleBulkAssign,
    handleRemoveMemberFromGroup,
  };
}
